import { Controller } from "@hotwired/stimulus";

// Diffusion temps réel des réponses GO4SIA (§8.5, §13.1).
// S'abonne au canal Go4siaConversationChannel et applique les deltas au
// fil de la conversation, avec auto-scroll intelligent.
//
// 🔴 **Le filet de sécurité de la diffusion live** — remise du 2026-09-06, maquette
// *Coach IA Juju*, écran 20c : *« au-delà de 15 s un filet qui va chercher la
// réponse, avec la promesse explicite que la question n'est pas décomptée deux
// fois »*. `ActionCable` peut ne jamais livrer `done` — coupure réseau, worker
// redémarré au milieu d'une diffusion — et la bulle reste sur « Juju réfléchit… »
// indéfiniment, sans qu'aucun geste ne la débloque.
//
// ⚠️ **`GO4LEARN_ASSISTANT_POLL_MS`, jamais un nombre en dur répété** : le délai
// initial et les relances qui suivent doivent rester la MÊME valeur, sinon le
// filet et son propre test finissent par mesurer deux durées différentes.
const POLL_DELAY_MS = 15000;

export default class extends Controller {
  static targets = ["messages"];
  static values = { conversationId: Number, interruptedLabel: String };

  connect() {
    this.userScrolledUp = false;
    this.scrollToBottom();
    this.pollTimers = new Map();

    const consumer = window._cableConsumer;
    if (consumer && this.conversationIdValue) {
      this.subscription = consumer.subscriptions.create(
        { channel: "Go4siaConversationChannel", id: this.conversationIdValue },
        { received: (data) => this.received(data) },
      );
    }

    if (this.hasMessagesTarget) {
      this._onScroll = () => this.onScroll();
      this.messagesTarget.addEventListener("scroll", this._onScroll);

      // Une bulle « streaming » peut déjà être là à la connexion (rechargement
      // pendant une génération) ou arriver plus tard, insérée par un
      // `turbo_stream` — d'où l'observateur plutôt qu'un seul passage ici.
      this.scheduleAllStreamingBubbles();
      this._observer = new MutationObserver(() => this.scheduleAllStreamingBubbles());
      this._observer.observe(this.messagesTarget, { childList: true, subtree: true });
    }
  }

  disconnect() {
    this.subscription?.unsubscribe();
    this._observer?.disconnect();
    this.pollTimers?.forEach((timer) => clearTimeout(timer));
    if (this.hasMessagesTarget && this._onScroll) {
      this.messagesTarget.removeEventListener("scroll", this._onScroll);
    }
  }

  // Arme le filet pour chaque bulle « streaming » qui n'en a pas déjà un.
  scheduleAllStreamingBubbles() {
    this.element.querySelectorAll("[data-status-url]").forEach((bubble) => {
      const id = bubble.id;
      if (!id || this.pollTimers.has(id)) return;

      this.pollTimers.set(id, setTimeout(() => this.pollStatus(bubble), POLL_DELAY_MS));
    });
  }

  // ⚠️ **Une seule LECTURE, jamais une écriture** : ce filet n'appelle que le GET
  // de statut. La question a déjà été posée et décomptée par `create_message` ;
  // le rejouer ici la décompterait une seconde fois.
  async pollStatus(bubble) {
    this.pollTimers.delete(bubble.id);
    // Le canal a fait son travail entre-temps, ou l'utilisateur a arrêté : rien à faire.
    if (!bubble.isConnected || bubble.dataset.aiStopped || bubble.dataset.aiDone) return;

    const url = bubble.dataset.statusUrl;
    if (!url) return;

    let data;
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`status ${response.status}`);
      data = await response.json();
    } catch (_) {
      // Panne réseau passagère : on retente au même délai plutôt que d'abandonner
      // l'apprenant sur un écran figé.
      this.pollTimers.set(bubble.id, setTimeout(() => this.pollStatus(bubble), POLL_DELAY_MS));
      return;
    }

    if (data.status === "pending" || data.status === "streaming") {
      this.pollTimers.set(bubble.id, setTimeout(() => this.pollStatus(bubble), POLL_DELAY_MS));
      return;
    }

    const messageId = Number(bubble.id.replace("ai_message_", ""));
    if (data.status === "failed") {
      this.received({ type: "error", message_id: messageId, message: data.content });
    } else {
      this.received({
        type: "done", message_id: messageId, html: data.html, cancelled: data.cancelled,
      });
    }
  }

  received(data) {
    const bubble = this.element.querySelector(`#ai_message_${data.message_id}`);
    if (!bubble) return;

    const content = bubble.querySelector("[data-ai-content]");
    const tool = bubble.querySelector("[data-ai-tool]");
    const cursor = bubble.querySelector("[data-ai-cursor]");
    const thinking = bubble.querySelector("[data-ai-thinking]");
    const stop = bubble.querySelector("[data-ai-stop]");

    switch (data.type) {
      case "delta":
        // Après un Stop, ou une fois le filet de 15 s passé par ce même message,
        // la bulle est figée : on ignore les deltas encore en vol.
        if (bubble.dataset.aiStopped || bubble.dataset.aiDone) break;
        thinking?.classList.add("hidden");
        tool?.classList.add("hidden");
        cursor?.classList.remove("hidden");
        content.textContent += data.text; // textContent = pas de risque XSS
        this.autoScroll();
        break;
      case "tool":
        thinking?.classList.add("hidden");
        if (tool) {
          tool.textContent = `🔍 ${data.tool}`;
          tool.classList.remove("hidden");
        }
        break;
      case "done":
        // ⚠️ Posé AVANT tout le reste : un filet de 15 s encore en vol qui se
        // déclenche juste après ce `done` légitime ne doit rien rejouer.
        bubble.dataset.aiDone = "1";
        cursor?.remove();
        stop?.remove();
        thinking?.classList.add("hidden");
        tool?.classList.add("hidden");
        // Remplace le texte brut streamé par le markdown rendu (sanitizé serveur,
        // donc innerHTML sûr — §13.9), et active le style « prose ».
        if (data.html != null) {
          content.innerHTML = data.html;
          content.classList.remove("whitespace-pre-wrap");
        }
        if (data.cancelled) this.markInterrupted(bubble, content);
        // GO4TEACH: the actions under an answer (« Ajouter à ma fiche »…) wait for its end.
        if (!data.cancelled) bubble.querySelector("[data-ai-after]")?.classList.remove("hidden");
        this.updateBalance(data);
        break;
      case "error":
        bubble.dataset.aiDone = "1";
        cursor?.remove();
        stop?.remove();
        thinking?.classList.add("hidden");
        tool?.classList.add("hidden");
        this.appendError(content, data.message);
        break;
    }
  }

  // Remplit le champ de saisie depuis une suggestion (chips de l'état vide).
  fill(event) {
    const text = event.currentTarget.dataset.suggestion;
    const input = this.element.querySelector("[data-assistant-composer-target='input']");
    if (input) {
      input.value = text;
      input.focus();
    }
  }

  // Interrompt la génération en cours (§13.7) : POST en arrière-plan (fetch),
  // SANS navigation. L'arrêt serveur est coopératif (throttle ≤ ~1s) et des deltas
  // peuvent encore arriver — on fige donc la bulle optimistiquement ici (texte
  // partiel + marqueur « interrompu », deltas tardifs ignorés) pour un effet
  // immédiat ; le serveur confirme ensuite via `done`.
  stop(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const url = btn.dataset.stopUrl;
    if (!url) return;

    btn.disabled = true;
    this.freezeBubble(btn);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", Accept: "application/json" },
      credentials: "same-origin",
    }).catch(() => {
      btn.disabled = false; // en cas d'échec réseau, on réautorise le clic
    });
  }

  // Fige la bulle en cours sur son texte partiel dès le clic Stop : marque la
  // bulle (pour ignorer les deltas tardifs), retire curseur/indicateur/bouton et
  // pose le marqueur « Réponse interrompue ».
  freezeBubble(btn) {
    const bubble = btn.closest('[id^="ai_message_"]');
    if (!bubble) return;
    bubble.dataset.aiStopped = "1";
    bubble.querySelector("[data-ai-cursor]")?.remove();
    bubble.querySelector("[data-ai-thinking]")?.classList.add("hidden");
    bubble.querySelector("[data-ai-stop]")?.remove();
    const content = bubble.querySelector("[data-ai-content]");
    if (content) this.markInterrupted(bubble, content);
  }

  // Copie le texte rendu d'une réponse dans le presse-papier (§13.7).
  copy(event) {
    const id = event.currentTarget.dataset.messageId;
    const bubble = this.element.querySelector(`#ai_message_${id}`);
    const text = bubble?.querySelector("[data-ai-content]")?.innerText || "";
    if (!text || !navigator.clipboard) return;

    navigator.clipboard.writeText(text).then(() => {
      const btn = event.currentTarget;
      const original = btn.innerHTML;
      btn.innerHTML = "✓";
      setTimeout(() => {
        btn.innerHTML = original;
      }, 1200);
    });
  }

  // Marque une réponse interrompue par l'utilisateur (§13.7) : insère un libellé
  // discret après le contenu, une seule fois (idempotent si le canal rejoue done).
  markInterrupted(bubble, content) {
    if (bubble.querySelector("[data-ai-interrupted]")) return;
    const marker = document.createElement("p");
    marker.dataset.aiInterrupted = "";
    marker.className =
      "text-xs text-gray-400 mt-1 inline-flex items-center gap-1";
    const icon = document.createElement("i");
    icon.className = "fa-solid fa-circle-stop";
    marker.appendChild(icon);
    marker.appendChild(
      document.createTextNode(this.interruptedLabelValue || ""),
    );
    content.insertAdjacentElement("afterend", marker);
  }

  appendError(content, message) {
    const p = document.createElement("p");
    p.className = "text-sm text-red-600 mt-1";
    p.textContent = message;
    content.appendChild(p);
  }

  updateBalance(data) {
    if (data.token_balance == null) return;
    const tokens = document.querySelector("[data-assistant-balance-target='tokens']");
    const messages = document.querySelector("[data-assistant-balance-target='messages']");
    if (tokens) tokens.textContent = new Intl.NumberFormat().format(data.token_balance);
    if (messages) messages.textContent = data.estimated_messages;
    const personal = document.querySelector('[data-assistant-balance-target="personal"]');
    if (personal && data.personal_balance != null) {
      personal.textContent = Number(data.personal_balance).toLocaleString("fr-FR");
    }
    // L'enveloppe mensuelle de la personne : compteur et barre.
    const used = document.querySelector('[data-assistant-balance-target="envelopeUsed"]');
    const bar = document.querySelector('[data-assistant-balance-target="envelopeBar"]');
    if (used && data.envelope_used != null) {
      used.textContent = Number(data.envelope_used).toLocaleString("fr-FR");
    }
    if (bar && data.envelope_used != null) {
      const cap = Number(bar.dataset.cap) || 1;
      bar.style.width = `${Math.min((data.envelope_used * 100) / cap, 100)}%`;
    }
  }

  onScroll() {
    const el = this.messagesTarget;
    this.userScrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > 80;
  }

  autoScroll() {
    if (!this.userScrolledUp) this.scrollToBottom();
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }
}
